import React from "react";
import { Link, useLocation } from "react-router-dom";

import { cn } from "../lib/utils";

import { useAppContext } from "../context/AppContext";

import { Icon } from "./Icon";

import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";

import { ThemeToggle } from "./ThemeToggle";
import { Input } from "./ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";

export const navItems = [
  { name: "Home", path: "/" },
  { name: "Editor", path: "/editor" },
  { name: "Capture", path: "/capture" },
  { name: "Templates", path: "/templates" },
  { name: "Pricing", path: "/pricing" },
  { name: "About", path: "/about" },
];

interface HeaderProps {
  /** Optional page title displayed next to the logo */
  title?: string;
}

export const Header: React.FC<HeaderProps> = ({ title }) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const { user, shape, setShape } = useAppContext();

  return (
    <header className="border-b">
      <div className="container flex h-16 items-center justify-between mx-auto px-4">
        <div className="flex items-center gap-2 font-bold text-xl">
          <Link to="/" className="flex items-center gap-2">
            <Icon name="camera" className="h-6 w-6" />
            <span>{title || "Canvas Editor"}</span>
          </Link>
        </div>
        <div className="flex items-center gap-2 flex-1 justify-center mx-4">
          <Input
            value={shape.name}
            onChange={(e) => setShape({ ...shape, name: e.target.value })}
            className="w-40"
          />
          <span className="text-sm text-muted-foreground">
            Size: {shape.size}KB
          </span>
          <Link
            to="/vault"
            className={cn(
              "text-sm font-medium hover:underline",
              currentPath === "/vault" && "underline",
            )}
          >
            Vault
          </Link>
        </div>
        {/* Desktop navigation */}
        <nav className="items-center gap-4 hidden md:flex">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                "text-sm font-medium hover:underline",
                currentPath === item.path && "underline",
              )}
            >
              {item.name}
            </Link>
          ))}
          <ThemeToggle />
          <a
            href="https://github.com"
            target="_blank"
            rel="noreferrer"
            className="text-muted-foreground hover:text-foreground"
          >
            <Icon name="github" className="h-5 w-5" />
            <span className="sr-only">GitHub</span>
          </a>
          <Link to="/user" className="flex items-center">
            <Avatar className="h-8 w-8">
              {user.avatar ? (
                <AvatarImage src={user.avatar} />
              ) : (
                <AvatarFallback>{user.name.charAt(0)}</AvatarFallback>
              )}
            </Avatar>
          </Link>
        </nav>
        {/* Mobile navigation */}
        <Sheet>
          <SheetTrigger asChild>
            <button className="md:hidden flex items-center justify-center rounded-md border border-input bg-background p-2 hover:bg-accent hover:text-accent-foreground">
              <Icon name="menu" className="h-6 w-6" />
              <span className="sr-only">Open navigation</span>
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="md:hidden w-64">
            <div className="flex flex-col gap-4 mt-4">
              <div className="space-y-2">
                <Input
                  value={shape.name}
                  onChange={(e) => setShape({ ...shape, name: e.target.value })}
                />
                <span className="text-sm text-muted-foreground">
                  Size: {shape.size}KB
                </span>
              </div>
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    "text-sm font-medium",
                    currentPath === item.path && "underline",
                  )}
                >
                  {item.name}
                </Link>
              ))}
              <ThemeToggle />
              <a
                href="https://github.com"
                target="_blank"
                rel="noreferrer"
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="github" className="h-5 w-5" />
                <span className="sr-only">GitHub</span>
              </a>
              <Link to="/user" className="flex items-center gap-2">
                <Avatar className="h-8 w-8">
                  {user.avatar ? (
                    <AvatarImage src={user.avatar} />
                  ) : (
                    <AvatarFallback>{user.name.charAt(0)}</AvatarFallback>
                  )}
                </Avatar>
                <span>{user.name}</span>
              </Link>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};
